import React, { createContext, useContext, useState, useEffect } from "react";
import { db } from "../firebase";
import { collection, query, where, getDocs } from "firebase/firestore";
import { tolerantId } from "../utils/firestoreHelpers";
import { getOccupanciesForUser } from "../utils/occupancies";
import { devLog } from "../utils/devLog";
import { esAlquilerDelEdificio } from "../utils/unidadesEdificio";

const BuildingContext = createContext();

// Firestore admite hasta 30 valores en un `in` y 30 disyunciones por consulta:
// dos `in` en la misma consulta se multiplican (2 variantes del userid × 15 del
// edificio = 30).
const MAX_DISYUNCIONES = 30;

/** Variantes número/string de cada id, sin repetir, para where(campo, "in", …). */
const variantesDeIds = (ids) => {
  const out = [];
  const vistas = new Set();
  (ids || []).forEach((id) => {
    if (id == null || String(id).trim() === "") return;
    tolerantId(String(id).trim()).forEach((v) => {
      const clave = `${typeof v}:${v}`;
      if (!vistas.has(clave)) {
        vistas.add(clave);
        out.push(v);
      }
    });
  });
  return out;
};

const enTandas = (lista, tam) => {
  const out = [];
  for (let i = 0; i < lista.length; i += tam) out.push(lista.slice(i, i + tam));
  return out;
};

const aEdificio = (docu) => {
  const data = docu.data();
  return {
    ...data,
    id: docu.id,
    latitude: data.latitude || data.lat || null,
    longitude: data.longitude || data.lng || null,
  };
};

/**
 * Edificios por buildingid (C5): where("buildingid", "in", variantes) en tandas
 * de 15 ids (≤ 30 variantes número/string). Así la consulta prueba la regla de
 * list (isMemberBid(resource.data.buildingid)); escanear la colección entera
 * no la prueba y se rechaza (la demo quedaba sin edificio). Si una tanda se
 * rechaza entera (un id de un vínculo viejo que ya no da acceso), se reintenta
 * id por id y se omiten solo los que fallan.
 */
async function fetchBuildingsByIds(buildingIdList) {
  const ids = [
    ...new Set(
      (buildingIdList || [])
        .filter((x) => x != null && String(x).trim() !== "")
        .map((x) => String(x).trim())
    ),
  ];
  const porDocId = new Map();
  const leer = async (grupo) => {
    const snap = await getDocs(
      query(collection(db, "building"), where("buildingid", "in", variantesDeIds(grupo)))
    );
    snap.docs.forEach((docu) => porDocId.set(docu.id, docu));
  };
  const sinAcceso = (id, err) =>
    console.warn("[BuildingContext] edificio sin acceso, se omite:", id, err?.code || err?.message || err);
  await Promise.all(
    enTandas(ids, MAX_DISYUNCIONES / 2).map(async (grupo) => {
      try {
        await leer(grupo);
      } catch (err) {
        if (grupo.length === 1) {
          sinAcceso(grupo[0], err);
          return;
        }
        await Promise.all(grupo.map((id) => leer([id]).catch((e) => sinAcceso(id, e))));
      }
    })
  );
  return [...porDocId.values()].map(aEdificio);
}

/**
 * Docs de `coleccion` de un usuario en ciertos edificios: dos `in` (usuario y
 * edificio) en tandas que no pasan de 30 disyunciones. Para el staff que mira
 * los vínculos de OTRA persona (C6: la regla solo se prueba con el campo de
 * edificio fijado en la consulta).
 */
async function docsDeUsuarioEnEdificios(coleccion, campoUsuario, userid, campoEdificio, buildingIds) {
  const porUsuario = tolerantId(userid);
  const variantes = variantesDeIds(buildingIds);
  if (variantes.length === 0) return [];
  const tam = Math.max(1, Math.floor(MAX_DISYUNCIONES / porUsuario.length));
  const snaps = await Promise.all(
    enTandas(variantes, tam).map((grupo) =>
      getDocs(
        query(
          collection(db, coleccion),
          where(campoUsuario, "in", porUsuario),
          where(campoEdificio, "in", grupo)
        )
      )
    )
  );
  const porId = new Map();
  snaps.forEach((s) => s.docs.forEach((d) => porId.set(d.id, d)));
  return [...porId.values()];
}

/** Tenencias (occupancies) de un usuario en ciertos edificios, con la forma de getOccupanciesForUser. */
const ocupacionesEnEdificios = async (userid, buildingIds) =>
  (await docsDeUsuarioEnEdificios("occupancies", "userid", userid, "buildingid", buildingIds)).map((d) => ({
    id: d.id,
    ...d.data(),
  }));

export const BuildingProvider = ({
  children,
  userEmail,
  userid: useridProp,     // opcional (recomendado pasar desde App)
  userType: userTypeProp, // opcional (recomendado pasar desde App)
  accessVersion = 0,      // sube cuando cambian los permisos (vincularMiCuenta): relee
}) => {
  const [buildings, setBuildings] = useState([]);
  const [selectedBuilding, setSelectedBuilding] = useState(null);
  const [userUnitsInBuilding, setUserUnitsInBuilding] = useState([]);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [loading, setLoading] = useState(true);
  // Indica si el edificio seleccionado tiene al menos un amenity configurado.
  // Se usa en Sidebar/Header del residente para ocultar la entrada de
  // Amenities cuando el edificio no tiene amenities. null = aun no resuelto.
  const [selectedBuildingHasAmenities, setSelectedBuildingHasAmenities] = useState(null);

  // "Ver como residente" (App.js): este proveedor queda anidado dentro del de
  // la sesión real y carga los edificios del VECINO, pero quien consulta es el
  // staff. Las reglas solo le dejan leer userbuilding y occupancies de sus
  // propios edificios (C6), así que esas consultas se acotan al edificio
  // elegido en el proveedor de afuera (VerComoResidente lo exige) o, si no
  // hay uno elegido, a los edificios de ese proveedor.
  const outer = useContext(BuildingContext);
  let previewScopeIds = null;
  if (outer && userTypeProp === 0) {
    previewScopeIds =
      outer.selectedBuilding?.buildingid != null
        ? [outer.selectedBuilding.buildingid]
        : (outer.buildings || []).map((b) => b.buildingid).filter((x) => x != null);
  }
  const previewScopeKey = previewScopeIds ? previewScopeIds.map(String).sort().join(",") : "";

  const refreshBuildings = async () => {
      if (!userEmail && !useridProp) return;

      setLoading(true);
      try {
        // 1) Resolver usuario base
        let effectiveUserType = userTypeProp;
        let effectiveUserid = useridProp ? String(useridProp) : "";

        if (effectiveUserType == null || !effectiveUserid) {
          // Fallback: buscar por email
          const usersQuery = query(collection(db, "users"), where("email", "==", userEmail));
          const userSnapshot = await getDocs(usersQuery);
          if (userSnapshot.empty) {
            setBuildings([]);
            setSelectedBuilding(null);
            setUserUnitsInBuilding([]);
            setSelectedUnit(null);
            setLoading(false);
            return;
          }
          const userData = userSnapshot.docs[0].data();
          effectiveUserType = Number(userData?.typeuser ?? 0);
          effectiveUserid = String(userData?.userid ?? "");
        }

        // 2) Superadmin => todos los edificios
        if (effectiveUserType === 2) {
          const buildingSnapshot = await getDocs(collection(db, "building"));
          const allBuildings = buildingSnapshot.docs.map((docu) => {
            const data = docu.data();
            return {
              ...data,
              id: docu.id,
              latitude: data.latitude || data.lat || null,
              longitude: data.longitude || data.lng || null,
              condition: "SuperAdmin",
            };
          });
          allBuildings.sort((a, b) => Number(a.buildingid) - Number(b.buildingid));
          setBuildings(allBuildings);
          setUserUnitsInBuilding([]);
          setSelectedUnit(null);
          setSelectedBuilding(null);
          return;
        }

        // 3) Admin, Empleado o Residente: conseguir lista de buildingids
        let buildingIds = []; // [{ id: buildingid, condition }]

        if (effectiveUserType === 3) {
          // --- EMPLEADO: consultar employeebuilding.employeeid ---
          const qEmp = query(
            collection(db, "employeebuilding"),
            where("employeeid", "in", tolerantId(effectiveUserid))
          );
          const empSnap = await getDocs(qEmp);
          const empDocs = empSnap.docs;

          buildingIds = empDocs.map((d) => ({
            id: d.data().building,
            condition: "Empleado",
          }));
        } else if (effectiveUserType === 1) {
          // --- ADMIN: consultar admin.userid (tolerante a tipo) ---
          const qAdmin = query(
            collection(db, "admin"),
            where("userid", "in", tolerantId(effectiveUserid))
          );
          const adminSnap = await getDocs(qAdmin);
          const adminDocs = adminSnap.docs;

          // Mapear los buildingIds encontrados
          buildingIds = adminDocs.map((d) => ({
            id: d.data().buildingid,
            condition: "Administrador",
          }));
          
          devLog("[BuildingContext] Admin userId:", effectiveUserid, "-> Edificios encontrados:", buildingIds.length);
        } else if (effectiveUserType === 4) {
          // Comercial (typeuser 4): no está atado a ningún edificio. Sin queries
          // (evita el error de permisos en `occupancies` y round-trips inútiles).
          buildingIds = [];
        } else {
          // --- RESIDENTE: userbuilding.user (incluye vínculos DESACTIVADOS
          // para preservar acceso histórico de solo-lectura del ocupante saliente)
          // + occupancies (para excluir edificios cuyo acceso fue bloqueado).
          // Los suyos los prueba isMine(user); en "ver como", el edificio. ---
          const ubDocs = previewScopeIds
            ? await docsDeUsuarioEnEdificios("userbuilding", "user", effectiveUserid, "building", previewScopeIds)
            : (
                await getDocs(
                  query(collection(db, "userbuilding"), where("user", "in", tolerantId(effectiveUserid)))
                )
              ).docs;

          // Agrupar por edificio: condition + si tiene algún vínculo ACTIVO.
          const ubByBuilding = new Map(); // bid(str) -> { condition, hasActive }
          ubDocs.forEach((d) => {
            const data = d.data();
            const bid = String(data.building);
            const prev = ubByBuilding.get(bid) || { condition: "", hasActive: false };
            if (data.active !== false && data.condition) prev.condition = data.condition;
            else if (!prev.condition && data.condition) prev.condition = data.condition;
            if (data.active !== false) prev.hasActive = true;
            ubByBuilding.set(bid, prev);
          });

          // Tenencias del residente (activas + cerradas) para detectar accesos
          // bloqueados por el admin (decisión #4: bloqueo por unidad).
          let myOcc = [];
          try {
            myOcc = previewScopeIds
              ? await ocupacionesEnEdificios(effectiveUserid, previewScopeIds)
              : await getOccupanciesForUser(effectiveUserid);
          } catch (occErr) {
            console.error("[BuildingContext] error cargando occupancies:", occErr);
            myOcc = [];
          }
          const nonBlockedBuildings = new Set();
          const occBuildings = new Set();
          myOcc.forEach((o) => {
            occBuildings.add(String(o.buildingid));
            if (o.accessBlocked !== true) nonBlockedBuildings.add(String(o.buildingid));
          });

          buildingIds = [];
          for (const [bid, info] of ubByBuilding.entries()) {
            // Ocultar el edificio SOLO si no tiene vínculo activo Y todas sus
            // tenencias ahí fueron bloqueadas (acceso totalmente revocado).
            if (!info.hasActive && occBuildings.has(bid) && !nonBlockedBuildings.has(bid)) {
              continue;
            }
            buildingIds.push({ id: bid, condition: info.condition, readOnly: !info.hasActive });
          }
        }

        if (buildingIds.length === 0) {
          setBuildings([]);
          setSelectedBuilding(null);
          setUserUnitsInBuilding([]);
          setSelectedUnit(null);
          return;
        }

        // 4) Traer datos de building por buildingid (tolerante a tipo; C5)
        const idList = buildingIds.map((b) => b.id);
        const rawBuildings = await fetchBuildingsByIds(idList);

        // Adjuntar condition + flag de solo-lectura (edificio histórico).
        const conditionById = new Map(
          buildingIds.map((b) => [String(b.id), b.condition])
        );
        const readOnlyById = new Map(
          buildingIds.map((b) => [String(b.id), !!b.readOnly])
        );
        const results = rawBuildings.map((b) => ({
          ...b,
          condition: conditionById.get(String(b.buildingid)) || "",
          isReadOnly: readOnlyById.get(String(b.buildingid)) || false,
        }));

        results.sort((a, b) => Number(a.buildingid) - Number(b.buildingid));
        setBuildings(results);
        
        // Para residentes (userType 0) y empleados (userType 3): auto-seleccionar el primer edificio
        // Para admins/superadmins: no preseleccionar (usan el filtro global)
        if ((effectiveUserType === 0 || effectiveUserType === 3) && results.length > 0) {
          setSelectedBuilding(results[0]);
        } else {
          setSelectedBuilding(null);
        }
        setUserUnitsInBuilding([]);
        setSelectedUnit(null);
      } catch (error) {
        console.error("Error fetching buildings:", error);
        setBuildings([]);
        setSelectedBuilding(null);
        setUserUnitsInBuilding([]);
        setSelectedUnit(null);
      } finally {
        setLoading(false);
      }
    };

  useEffect(() => {
    refreshBuildings();
  }, [userEmail, useridProp, userTypeProp, accessVersion, previewScopeKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Para residentes: cargar unidades del usuario en el edificio seleccionado
  useEffect(() => {
    const fetchUserUnits = async () => {
      if (userTypeProp !== 0 || !selectedBuilding?.buildingid) {
        setUserUnitsInBuilding([]);
        setSelectedUnit(null);
        return;
      }

      let effectiveUserid = useridProp ? String(useridProp) : "";
      if (!effectiveUserid && userEmail) {
        const usersQuery = query(collection(db, "users"), where("email", "==", userEmail));
        const userSnapshot = await getDocs(usersQuery);
        if (userSnapshot.empty) {
          setUserUnitsInBuilding([]);
          setSelectedUnit(null);
          return;
        }
        effectiveUserid = String(userSnapshot.docs[0].data()?.userid ?? "");
      }
      if (!effectiveUserid) {
        setUserUnitsInBuilding([]);
        setSelectedUnit(null);
        return;
      }

      try {
        const apartmentsQuery = query(
          collection(db, "apartments"),
          where("building", "in", tolerantId(selectedBuilding.buildingid))
        );
        const snap = await getDocs(apartmentsQuery);
        const all = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        const uidStr = String(effectiveUserid);
        const uidNum = Number(effectiveUserid);
        const matchUser = (val) =>
          val != null &&
          (String(val) === uidStr || (!isNaN(uidNum) && Number(val) === uidNum));

        // Tenencias del residente en este edificio (para unidades históricas y
        // para excluir las que el admin bloqueó — decisión #4). Con el edificio
        // fijado en la consulta pasa igual para el vecino (isMine) que para el
        // staff en "ver como" (C6).
        let myOcc = [];
        try {
          myOcc = await ocupacionesEnEdificios(effectiveUserid, [selectedBuilding.buildingid]);
        } catch (occErr) {
          console.error("Error cargando occupancies del residente:", occErr);
          myOcc = [];
        }
        const occByAptId = new Map(); // aptId(str) -> [occ]
        myOcc.forEach((o) => {
          if (String(o.buildingid) !== String(selectedBuilding.buildingid)) return;
          const k = String(o.apartmentId);
          if (!occByAptId.has(k)) occByAptId.set(k, []);
          occByAptId.get(k).push(o);
        });

        const units = all
          .map((apt) => {
            const isCurrent =
              matchUser(apt.owner) ||
              matchUser(apt.tenant) ||
              matchUser(apt.ownerSecondary) ||
              matchUser(apt.tenantSecondary);
            const occs = occByAptId.get(String(apt.id)) || [];
            const allBlocked = occs.length > 0 && occs.every((o) => o.accessBlocked === true);
            // Mostrar si es ocupante vigente, o tiene una tenencia histórica NO
            // bloqueada. Ocultar si su único vínculo es una tenencia bloqueada.
            const include = (isCurrent || occs.length > 0) && !(!isCurrent && allBlocked);
            if (!include) return null;

            let userRole;
            if (matchUser(apt.owner) || matchUser(apt.ownerSecondary)) userRole = "Propietario";
            else if (matchUser(apt.tenant) || matchUser(apt.tenantSecondary)) userRole = "Inquilino";
            else {
              const lastOcc = occs
                .slice()
                .sort((a, b) => String(b.dateFrom || "").localeCompare(String(a.dateFrom || "")))[0];
              userRole = lastOcc?.role === "owner" ? "Propietario" : "Inquilino";
            }
            return {
              ...apt,
              userRole,
              isHistorical: !isCurrent,
              isReadOnly: !isCurrent,
              // Períodos que el residente ocupó esta unidad (para limitar los
              // documentos visibles a su histórico).
              occupancyPeriods: occs.map((o) => ({
                dateFrom: o.dateFrom || null,
                dateTo: o.dateTo || null,
                accessBlocked: o.accessBlocked === true,
              })),
            };
          })
          .filter(Boolean);
        setUserUnitsInBuilding(units);

        setSelectedUnit((prev) => {
          if (units.length === 0) return null;
          if (units.length === 1) return units[0];
          const stillValid = prev && units.some((u) => u.id === prev.id);
          return stillValid ? prev : units[0];
        });
      } catch (err) {
        console.error("Error fetching user units:", err);
        setUserUnitsInBuilding([]);
        setSelectedUnit(null);
      }
    };

    fetchUserUnits();
  }, [selectedBuilding, useridProp, userEmail, userTypeProp]);

  // Resolver si el edificio seleccionado tiene amenities configurados.
  // Sidebar/Header del residente ocultan la entrada de Amenities cuando
  // este valor es false (edificio sin amenities en `amenitiesbuilding`).
  useEffect(() => {
    const checkAmenities = async () => {
      if (!selectedBuilding?.buildingid) {
        setSelectedBuildingHasAmenities(null);
        return;
      }
      try {
        const amenQ = query(
          collection(db, "amenitiesbuilding"),
          where("buildingid", "in", tolerantId(selectedBuilding.buildingid))
        );
        const amenSnap = await getDocs(amenQ);
        // Existe al menos un amenity (sin filtrar por status: si esta en la
        // coleccion ya cuenta como "tiene amenities", aunque este inactivo).
        setSelectedBuildingHasAmenities(amenSnap.size > 0);
      } catch (err) {
        console.error("Error checking amenities for building:", err);
        // Falla cerrada: si no se puede consultar, mostrar la tab (preferimos
        // un falso positivo a esconder algo que el residente espera ver).
        setSelectedBuildingHasAmenities(true);
      }
    };

    checkAmenities();
  }, [selectedBuilding]);

  // Todas las unidades vigentes del usuario en este edificio son "Alquiler del
  // edificio": lo que paga es alquiler, no gastos comunes (cambia el menú y los
  // títulos del residente).
  const unidadesVigentes = userUnitsInBuilding.filter((u) => !u.isHistorical);
  const soloAlquilerEdificio = unidadesVigentes.length > 0 && unidadesVigentes.every(esAlquilerDelEdificio);

  return (
    <BuildingContext.Provider value={{
      buildings,
      selectedBuilding,
      setSelectedBuilding,
      userUnitsInBuilding,
      selectedUnit,
      setSelectedUnit,
      loading,
      refreshBuildings,
      selectedBuildingHasAmenities,
      soloAlquilerEdificio,
    }}>
      {children}
    </BuildingContext.Provider>
  );
};

export const useBuilding = () => useContext(BuildingContext);
